import { useEffect, useState } from "react";
import { FormField } from "@NP-Webber/crm-kit";
import "@NP-Webber/crm-kit/dist/formkit/FormKit.css";
import {
  meetingsApi,
  type AssignableUser,
  type MeetingActionItem,
  type MeetingFull,
} from "../../../api/meetingsApi";
import { toClientApiError } from "../../../api/httpClient";
import { toDateInputValue } from "../constants";
import { printTeamMeeting } from "../teamMeetingPrint";
import { ActionItemsTable, type ActionDraft } from "./ActionItemsTable";
import { PreviousActionsBanner } from "./PreviousActionsBanner";

type TopicDraft = {
  clientKey: string;
  id?: string;
  agendaItemId?: string | null;
  title: string;
  summary: string;
  decisions: string;
};

type Props = {
  meeting: MeetingFull | null;
  users: AssignableUser[];
  canManage: boolean;
  onRefresh: () => void;
  onFinalized: () => void;
};

export function SummaryPanel({
  meeting,
  users,
  canManage,
  onRefresh,
  onFinalized,
}: Props) {
  const [openTopics, setOpenTopics] = useState<Record<string, boolean>>({});
  const [topics, setTopics] = useState<TopicDraft[]>([]);
  const [actionItems, setActionItems] = useState<ActionDraft[]>([]);
  const [prevActions, setPrevActions] = useState<MeetingActionItem[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!meeting) return;
    const agenda = meeting.agendaItems || [];
    const existingTopics = meeting.topics || [];
    if (existingTopics.length) {
      setTopics(
        existingTopics.map((t) => ({
          ...t,
          clientKey: t.id,
          summary: t.summary || "",
          decisions: t.decisions || "",
        })),
      );
    } else {
      setTopics(
        agenda.map((a) => ({
          clientKey: `agenda-${a.id}`,
          agendaItemId: a.id,
          title: a.title,
          summary: "",
          decisions: "",
        })),
      );
    }
    setActionItems(
      (meeting.actionItems || []).map((a) => ({
        ...a,
        clientKey: a.id,
        technicalDetails: a.technicalDetails || "",
        assignedToUserId: a.assignedToUserId || "",
        dueDate: toDateInputValue(a.dueDate),
        createTask: a.createTask !== false,
      })),
    );
    meetingsApi
      .getOpenActions(meeting.id)
      .then((items) => setPrevActions(items || []))
      .catch(() => setPrevActions([]));
  }, [meeting]);

  if (!meeting) {
    return <div className="tm-empty">אין ישיבה לסיכום</div>;
  }

  const toggleTopic = (key: string) =>
    setOpenTopics((prev) => ({ ...prev, [key]: !prev[key] }));

  const updateTopic = (idx: number, field: keyof TopicDraft, value: string) => {
    const next = [...topics];
    next[idx] = { ...next[idx], [field]: value };
    setTopics(next);
  };

  const addExtraTopic = () => {
    setTopics([
      ...topics,
      {
        clientKey: `extra-${Date.now()}`,
        title: "נושא נוסף",
        summary: "",
        decisions: "",
        agendaItemId: null,
      },
    ]);
  };

  const handleVerify = async (actionId: string) => {
    try {
      await meetingsApi.updateActionItem(actionId, {
        verified: true,
        status: "done",
      });
      setPrevActions((prev) => prev.filter((a) => a.id !== actionId));
    } catch (err) {
      window.alert(toClientApiError(err).message || "שגיאה");
    }
  };

  const handleFinalize = async (sendSummary = true) => {
    setSaving(true);
    try {
      await meetingsApi.finalizeSummary(meeting.id, {
        topics: topics.map((t, i) => ({
          agendaItemId: t.agendaItemId,
          title: t.title,
          summary: t.summary,
          decisions: t.decisions,
          sortOrder: i + 1,
          clientKey: t.clientKey,
        })),
        actionItems: actionItems
          .filter((a) => a.description.trim())
          .map((a) => ({
            description: a.description,
            technicalDetails: a.technicalDetails,
            assignedToUserId: a.assignedToUserId || null,
            dueDate: a.dueDate || null,
            createTask: a.createTask !== false,
            topicKey: a.topicKey || undefined,
          })),
        sendSummary,
      });
      onFinalized();
    } catch (err) {
      window.alert(toClientApiError(err).message || "שגיאה בשמירה");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <PreviousActionsBanner
        actions={prevActions}
        onVerify={(id) => {
          void handleVerify(id);
        }}
        onRefresh={onRefresh}
      />
      <div className="tm-panel">
        <div className="tm-panel__head">
          <h3 className="tm-panel__title">סיכום לפי נושאים</h3>
          {canManage ? (
            <button
              type="button"
              className="tm-btn tm-btn--outline tm-btn--sm"
              onClick={addExtraTopic}
            >
              + נושא שלא בסדר יום
            </button>
          ) : null}
        </div>
        {topics.map((t, idx) => {
          const key = t.clientKey || t.id || String(idx);
          const isOpen = openTopics[key] !== false;
          return (
            <div key={key} className="tm-topic-card">
              <div
                className="tm-topic-card__head"
                onClick={() => toggleTopic(key)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") toggleTopic(key);
                }}
                role="button"
                tabIndex={0}
              >
                <span>{t.title}</span>
                <span>{isOpen ? "▲" : "▼"}</span>
              </div>
              {isOpen ? (
                <div className="tm-topic-card__body">
                  {canManage && !t.agendaItemId ? (
                    <FormField
                      label="כותרת נושא"
                      value={t.title}
                      onChange={(v) => updateTopic(idx, "title", v)}
                    />
                  ) : null}
                  <FormField
                    label="סיכום"
                    value={t.summary || ""}
                    onChange={(v) => updateTopic(idx, "summary", v)}
                    rows={3}
                    disabled={!canManage}
                  />
                  <FormField
                    label="החלטות"
                    value={t.decisions || ""}
                    onChange={(v) => updateTopic(idx, "decisions", v)}
                    rows={2}
                    disabled={!canManage}
                  />
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
      <ActionItemsTable
        items={actionItems}
        users={users}
        onChange={setActionItems}
        canManage={canManage}
      />
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button
          type="button"
          className="tm-btn tm-btn--outline"
          onClick={() =>
            printTeamMeeting({
              ...meeting,
              topics: topics.map((t) => ({
                id: t.id,
                title: t.title,
                summary: t.summary,
                decisions: t.decisions,
              })),
              actionItems: actionItems.map((a) => ({
                id: a.id,
                description: a.description,
                technicalDetails: a.technicalDetails,
                assignedToUserId: a.assignedToUserId,
                dueDate: a.dueDate,
                createTask: a.createTask,
                topicKey: a.topicKey,
              })),
            })
          }
        >
          הדפסה / PDF
        </button>
        {canManage ? (
          <>
            <button
              type="button"
              className="tm-btn tm-btn--primary"
              onClick={() => {
                void handleFinalize(true);
              }}
              disabled={saving}
            >
              {saving ? "שומר…" : "שמור ושלח סיכום"}
            </button>
            <button
              type="button"
              className="tm-btn tm-btn--outline"
              onClick={() => {
                void handleFinalize(false);
              }}
              disabled={saving}
            >
              שמור בלי שליחה
            </button>
          </>
        ) : null}
      </div>
    </>
  );
}
